import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, CartesianGrid } from "recharts";
import { TrendingUp, MailOpen, MousePointerClick, AlertTriangle, ShieldOff, Trophy, Percent } from "lucide-react";
import { Button } from "@/components/ui/button";

const COLORS = { emerald: "#16A34A", sun: "#F5B841", crimson: "#DC2626", slate: "#6B7280", brand: "#111827" };

function Kpi({ label, value, sub, icon: Icon, tone = "emerald", testid }) {
  const tones = {
    emerald: "text-emerald-brand bg-emerald-brand/12",
    sun: "text-yellow-700 bg-sun/25",
    crimson: "text-crimson bg-crimson/12",
    slate: "text-muted-foreground bg-muted",
  };
  return (
    <div className="rounded-2xl border border-border bg-white p-4 flex items-start justify-between" data-testid={testid}>
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className="font-display text-3xl font-semibold mt-1">{value}</div>
        {sub && <div className="text-[11px] font-mono text-muted-foreground mt-0.5">{sub}</div>}
      </div>
      <div className={`h-9 w-9 rounded-xl grid place-items-center ${tones[tone]}`}>
        <Icon className="h-4 w-4" strokeWidth={1.75} />
      </div>
    </div>
  );
}

export default function BulkAnalytics() {
  const { activeCompanyId } = useAuth();
  const [days, setDays] = useState(30);
  const [overview, setOverview] = useState(null);
  const [rows, setRows] = useState([]);
  const [top, setTop] = useState([]);

  const load = async () => {
    const params = { ...(activeCompanyId ? { company_id: activeCompanyId } : {}), days };
    const [o, c, t] = await Promise.all([
      api.get("/bulk/analytics/overview", { params }),
      api.get("/bulk/analytics/campaigns", { params }),
      api.get("/bulk/analytics/subject-leaderboard", { params }),
    ]);
    setOverview(o.data);
    setRows(c.data);
    setTop(t.data.top_subjects || []);
  };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); /* eslint-disable-next-line */ }, [activeCompanyId, days]);

  const r = overview?.rates || {};
  const c = overview?.counts || {};
  const funnel = [
    { name: "Sent", value: c.total_sent || 0, fill: COLORS.brand },
    { name: "Opened", value: c.opened || 0, fill: COLORS.emerald },
    { name: "Clicked", value: c.clicked || 0, fill: COLORS.sun },
  ];
  const outcomes = [
    { name: "Delivered", value: (c.total_sent || 0) - (c.bounced || 0), fill: COLORS.emerald },
    { name: "Bounced", value: c.bounced || 0, fill: COLORS.crimson },
    { name: "Failed", value: c.failed || 0, fill: COLORS.slate },
    { name: "Suppressed", value: c.suppressed || 0, fill: COLORS.sun },
  ].filter((x) => x.value > 0);

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="bulk-analytics-page">
      <div className="flex items-center gap-2 flex-wrap">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Window</div>
          <div className="inline-flex rounded-xl border border-border overflow-hidden text-xs mt-1" data-testid="analytics-window-toggle">
            {[7, 14, 30, 90].map((d) => (
              <button key={d} onClick={() => setDays(d)} className={`px-3 py-1.5 font-mono uppercase tracking-widest ${days === d ? "bg-emerald-brand text-white" : "text-muted-foreground hover:bg-muted"}`} data-testid={`window-${d}`}>{d}d</button>
            ))}
          </div>
        </div>
        <div className="ml-auto text-[11px] font-mono text-muted-foreground">{overview?.total_campaigns ?? 0} campaigns · {c.total_sent ?? 0} deliveries · last {days} days</div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <Kpi label="Open rate" value={`${r.open_rate ?? 0}%`} sub={`${c.opened ?? 0} opens`} icon={MailOpen} tone="emerald" testid="kpi-open-rate" />
        <Kpi label="Click rate" value={`${r.click_rate ?? 0}%`} sub={`${c.clicked ?? 0} clicks`} icon={MousePointerClick} tone="sun" testid="kpi-click-rate" />
        <Kpi label="CTOR" value={`${r.ctor ?? 0}%`} sub="click-to-open" icon={Percent} tone="emerald" testid="kpi-ctor" />
        <Kpi label="Bounce rate" value={`${r.bounce_rate ?? 0}%`} sub={`${c.bounced ?? 0} bounces`} icon={AlertTriangle} tone="crimson" testid="kpi-bounce-rate" />
        <Kpi label="Unsub rate" value={`${r.unsub_rate ?? 0}%`} sub={`${c.unsubscribed ?? 0} unsubs`} icon={ShieldOff} tone="crimson" testid="kpi-unsub-rate" />
        <Kpi label="Delivered" value={c.total_sent ?? 0} sub={`${overview?.total_campaigns ?? 0} campaigns`} icon={TrendingUp} tone="slate" testid="kpi-delivered" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[2fr_1fr] gap-4">
        <div className="rounded-2xl border border-border bg-white p-4">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-3">Sends · opens · clicks</div>
          <div className="h-64" data-testid="analytics-timeseries">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={overview?.series || []} margin={{ top: 4, right: 10, left: -10, bottom: 4 }}>
                <defs>
                  <linearGradient id="gs" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={COLORS.emerald} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={COLORS.emerald} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="go" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={COLORS.sun} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={COLORS.sun} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                <XAxis dataKey="day" tick={{ fontSize: 10 }} tickFormatter={(d) => d?.slice(5)} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e5e7eb", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Area type="monotone" dataKey="sent" stroke={COLORS.brand} strokeWidth={2} fill="url(#gs)" />
                <Area type="monotone" dataKey="opened" stroke={COLORS.emerald} strokeWidth={2} fill="url(#gs)" />
                <Area type="monotone" dataKey="clicked" stroke={COLORS.sun} strokeWidth={2} fill="url(#go)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-2xl border border-border bg-white p-4">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-3">Delivery outcomes</div>
          <div className="h-64" data-testid="analytics-outcomes">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={outcomes} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={2}>
                  {outcomes.map((entry, index) => <Cell key={index} fill={entry.fill} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e5e7eb", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-white">
        <div className="p-4 border-b border-border flex items-center gap-2">
          <Trophy className="h-4 w-4 text-yellow-600" />
          <h3 className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Best-performing subjects</h3>
          <span className="text-[11px] text-muted-foreground ml-auto">Top {top.length} by open rate</span>
        </div>
        {top.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground" data-testid="no-leaderboard">No opens yet — send a campaign to see the leaderboard.</div>
        ) : (
          <table className="tbl w-full" data-testid="leaderboard-table">
            <thead><tr><th>Rank</th><th>Campaign</th><th>Subject</th><th>Sent</th><th>Open %</th><th>Click %</th><th>CTOR</th><th></th></tr></thead>
            <tbody>
              {top.map((s, i) => (
                <tr key={s.campaign_id} data-testid={`leaderboard-row-${i}`}>
                  <td className="font-display font-semibold text-lg text-yellow-600">{i + 1}</td>
                  <td className="font-medium">{s.name}</td>
                  <td className="text-xs text-muted-foreground truncate max-w-[360px]">{s.subject}</td>
                  <td className="font-mono text-xs">{s.sent}</td>
                  <td className="font-mono text-emerald-brand">{s.open_rate}%</td>
                  <td className="font-mono">{s.click_rate}%</td>
                  <td className="font-mono">{s.ctor}%</td>
                  <td className="text-right"><Link to={`/bulk/campaigns/${s.campaign_id}`} className="text-xs text-emerald-brand hover:underline">Open →</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="rounded-2xl border border-border bg-white">
        <div className="p-4 border-b border-border"><h3 className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">All campaigns · performance</h3></div>
        <div className="max-h-[520px] overflow-y-auto">
          <table className="tbl w-full" data-testid="analytics-campaigns-table">
            <thead className="sticky top-0 bg-white z-10"><tr><th>Campaign</th><th>Sent</th><th>Open %</th><th>Click %</th><th>Bounce %</th><th>Unsub %</th><th>CTOR</th><th></th></tr></thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} data-testid={`analytics-camp-${c.id}`}>
                  <td>
                    <div className="font-medium">{c.name}</div>
                    <div className="text-[11px] font-mono text-muted-foreground truncate max-w-[300px]">{c.subject}</div>
                  </td>
                  <td className="font-mono text-xs">{c.sent}/{c.total}</td>
                  <td className="font-mono text-emerald-brand">{c.open_rate}%</td>
                  <td className="font-mono">{c.click_rate}%</td>
                  <td className="font-mono text-crimson">{c.bounce_rate}%</td>
                  <td className="font-mono text-crimson">{c.unsub_rate}%</td>
                  <td className="font-mono">{c.ctor}%</td>
                  <td className="text-right"><Link to={`/bulk/campaigns/${c.id}`} className="text-xs text-emerald-brand hover:underline">Open →</Link></td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan="8" className="text-center text-muted-foreground py-8">No campaign data yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
